import React from 'react';
import { api } from '../api.js';
import { Empty, Icon, Loading, PH, Panel } from './components.jsx';
import { Button, Pill, Switch } from './ds/fgcp-ds.jsx';
import * as T from '../time.js';

/**
 * 메일 에이전트(시범, 계획 문서 5.6) — 내 기능 켜기·끄기·일정 자동 등록(6f), 제안 모음(일정 등록·무시), 처리 기록(요약·한 일·되돌리기·다시 시도).
 * 일정은 내 캘린더에 들어간다(6f).
 * 메일 원본은 보관하지 않는다 — 요약과 한 일만. 할 수 있는 일은 서버가 정한다(actions·can).
 */
const ACTION_LABEL = { notify: '나에게 알림', calendar: '일정', wiki: '위키' };
const STATUS = { proposed: ['제안', 'warn'], done: ['완료', 'ok'], dismissed: ['무시함', 'neutral'], undone: ['되돌림', 'neutral'], cancelled: ['취소됨', 'neutral'], failed: ['실패', 'crit'] };

function actionText(a) {
  const p = a.payload || {};
  if (a.kind === 'calendar') return `${p.subject} · ${T.dateTime(p.start)}–${T.hm(p.end)}${p.location ? ` · ${p.location}` : ''}`;
  if (a.kind === 'notify') return p.title;
  return '';
}

/**
 * 켜기·끄기 — DS Switch(누르면 바로 적용). 누르면 바로 그 값으로 보이고(서버 응답을 기다리는 동안 다시 누르지 못한다), 서버 값이 오면 그 값으로 맞춘다 — 실패면 되돌아간다
 */
function OnOff({ name, on, disabled, onChange, label }) {
  const [shown, setShown] = React.useState(on);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setShown(on); }, [on]);
  const pick = (v) => { setShown(v); setBusy(true); Promise.resolve(onChange(v)).finally(() => setBusy(false)); };
  return <span data-onoff={name} data-on={on ? 'on' : 'off'} style={{ display: 'inline-flex', flexShrink: 0 }}>
    <Switch aria-label={label} checked={shown} disabled={disabled || busy} onChange={pick} />
  </span>;
}

export function MailAgent({ onToast }) {
  const [d, setD] = React.useState(null);
  const [items, setItems] = React.useState(null);
  const load = React.useCallback(() => {
    api.get('/api/v1/mail-agent').then(setD).catch(e => onToast(e.message));
    api.get('/api/v1/mail-agent/items?limit=50').then(r => setItems(r.items)).catch(e => onToast(e.message));
  }, [onToast]);
  React.useEffect(() => { load(); }, [load]);
  const act = (path, msg) => api.post(path, {}).then(() => { onToast(msg); load(); }).catch(e => { onToast(e.message); load(); });
  const calendarAuto = (c, on) => api.put(`/api/v1/mail-agent/capabilities/${c.id}/calendar-auto`, { on })
    .then(() => { onToast(on ? `'${c.name}'의 일정을 내 캘린더에 바로 넣습니다` : `'${c.name}'의 일정은 제안으로 받습니다`); load(); }).catch(e => { onToast(e.message); load(); });
  if (!d) return <div className="body"><Loading title="불러오는 중" /></div>;
  return (
    <>
      <div className="head"><h1>메일 에이전트</h1>
        <div className="meta" style={{ fontSize: 12, color: 'var(--ink-3)' }}>켠 기능에 맞춰 메일을 읽고 요약·알림·일정 제안을 합니다. 메일 원본은 보관하지 않습니다.</div></div>
      <div className="body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <Panel>
          <PH title="내 기능" sub="관리자가 승인한 기능 중 나에게 맞는 것 — 켠 기능만 내 메일에 적용됩니다. 꺼도 처리 기록은 남습니다" />
          <div data-mail-capabilities style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {d.capabilities.length === 0 && <Empty title="켤 수 있는 기능이 없습니다" />}
            {d.capabilities.map(c => (
              <div key={c.id} data-mail-capability={c.key} style={{ border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)', overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px' }}>
                  <div style={{ width: 30, height: 30, borderRadius: 'var(--r-sm)', display: 'grid', placeItems: 'center', flexShrink: 0,
                    background: c.enabled ? 'var(--acc-tint)' : 'var(--surface-2)', color: c.enabled ? 'var(--acc)' : 'var(--ink-3)' }}><Icon name="inbox" size={16} /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13.5 }}>{c.name}
                      {c.enabled && !c.applies && <Pill tone="warn" dot>{c.status !== 'approved' ? '관리자 승인 대기' : '대상 아님'}</Pill>}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 3, lineHeight: 1.5 }}>{c.description}</div>
                  </div>
                  <OnOff name={`cap-${c.id}`} label={c.name} on={c.enabled} disabled={!c.actions.enable && !c.actions.disable}
                    onChange={(v) => act(`/api/v1/mail-agent/capabilities/${c.id}/${v ? 'enable' : 'disable'}`, v ? `'${c.name}'을(를) 켰습니다` : `'${c.name}'을(를) 껐습니다`)} />
                </div>
                {c.calendar_auto && (
                  <div data-mail-calendar-auto={c.myCalendarAuto ? 'on' : 'off'}
                    style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px 10px 56px', background: 'var(--surface-2)', borderTop: '1px solid var(--line)' }}>
                    <Icon name="calendar" size={14} color={c.myCalendarAuto ? 'var(--acc)' : 'var(--ink-3)'} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: c.enabled ? 'var(--ink)' : 'var(--ink-3)' }}>일정 자동 등록</div>
                      <div style={{ fontSize: 11.5, color: 'var(--ink-3)', marginTop: 2 }}>
                        {!c.enabled ? `'${c.name}'을(를) 켜면 쓸 수 있습니다` : c.myCalendarAuto
                          ? '찾은 미팅·마감을 내 캘린더에 바로 넣습니다 — 회사 밖 메일 포함, 처리 기록에서 되돌리기' : '일정은 제안으로 받고, 등록을 누르면 내 캘린더에 들어갑니다'}</div>
                    </div>
                    <OnOff name={`cal-${c.id}`} label={`${c.name} 일정 자동 등록`} on={c.myCalendarAuto} disabled={!c.actions.calendarAutoOn && !c.actions.calendarAutoOff}
                      onChange={(v) => calendarAuto(c, v)} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </Panel>
        <Panel>
          <PH title="제안" sub={`정하지 않은 제안 ${d.proposals.length}건 — 등록하면 내 캘린더에 들어갑니다`} />
          <div data-mail-proposals style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {d.proposals.length === 0 && <Empty title="정할 제안이 없습니다" />}
            {d.proposals.map(a => (
              <div key={a.id} data-mail-proposal={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)' }}>
                <Pill tone="neutral" dot={false}>{ACTION_LABEL[a.kind]}</Pill>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600 }}>{actionText(a)}</div>
                  <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>메일 '{a.item_subject}'{a.sender_name ? ` · ${a.sender_name}` : ''}{a.external ? ' · 외부' : ''}</div>
                </div>
                {a.can.execute && <Button size="sm" onClick={() => act(`/api/v1/mail-agent/actions/${a.id}/execute`, '등록했습니다')}>등록</Button>}
                {a.can.dismiss && <Button size="sm" variant="secondary" onClick={() => act(`/api/v1/mail-agent/actions/${a.id}/dismiss`, '무시했습니다')}>무시</Button>}
              </div>
            ))}
          </div>
        </Panel>
        <Panel>
          <PH title="처리 기록" sub="최근 50건 — 요약과 에이전트가 한 일" />
          <div data-mail-items style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {!items ? <Loading title="불러오는 중" /> : items.length === 0 ? <Empty title="처리한 메일이 없습니다" /> : items.map(i => (
              <div key={i.id} data-mail-item={i.id} style={{ padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 'var(--r-ctl)' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                  <b style={{ fontSize: 13 }}>{i.subject || '(제목 없음)'}</b>
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>{i.sender_name ?? ''}{i.sender_domain ? ` <${i.sender_domain}>` : ''}{i.external ? ' · 외부' : ''} · {T.dateTime(i.received_at)}</span>
                  {i.tags.map(t => <Pill key={t} tone="neutral" dot={false}>{t}</Pill>)}
                </div>
                {i.summary && <div style={{ fontSize: 12.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{i.summary}</div>}
                {i.actions.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
                  {i.actions.map(a => {
                    const [l, tone] = STATUS[a.status] ?? [a.status, 'neutral'];
                    return (
                      <div key={a.id} data-mail-action={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
                        <Pill tone={tone} dot>{l}</Pill><span>{ACTION_LABEL[a.kind]} — {actionText(a)}</span>
                        {a.kind === 'calendar' && a.status === 'done' && <span style={{ fontSize: 11, color: a.calendar_error && !a.calendar_sync_due ? 'var(--crit)' : 'var(--ink-3)' }}
                          data-mail-calendar-state={a.calendar_error && !a.calendar_sync_due ? 'failed' : a.in_calendar ? 'in' : 'pending'}>
                          {a.calendar_auto ? '자동 등록 · ' : ''}{a.calendar_error && !a.calendar_sync_due ? `캘린더 등록 실패(${a.calendar_error})` : a.in_calendar ? '내 캘린더에 있음' : '캘린더에 넣는 중'}</span>}
                        {a.can.retry && <Button size="sm" variant="secondary" onClick={() => act(`/api/v1/mail-agent/actions/${a.id}/retry`, '다시 시도합니다')}>다시 시도</Button>}
                        {a.can.undo && <Button size="sm" variant="secondary" onClick={() => act(`/api/v1/mail-agent/actions/${a.id}/undo`, '되돌렸습니다')}>되돌리기</Button>}
                      </div>
                    );
                  })}
                </div>}
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </>
  );
}
